import React from "react";
import Meta from "../Meta";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { restPeriodTitle, restPeriodRowLabel } from "../../../services/restPeriodDisplay";
import {
    getZmanimLabels, fmtTime, fmtGreg, fmtShortDate, useZmanim,
} from "../components/zmanimData";

/**
 * Page « Horaires halakhiques » (zmanim) — thème 2026.
 *
 * Reprend la mise en page de `themes/Travel/JewishZmanim` (sélecteur de date,
 * carte des zmanim du jour avec le prochain mis en avant, carte du prochain
 * Chabbat) mais habillée avec les tokens du thème (--primary, --surface, --ink…)
 * → s'adapte à la palette de chaque org.
 *
 * Le chrome (header/footer) est posé par `ThemeRoot`, contrairement à Travel où
 * la page se les portait elle-même.
 */

const styles = `
.t26-zmanim {
  padding: 0 clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px);
  max-width: 760px;
  margin-inline: auto;
}

/* ── Sélecteur de date ── */
.t26-zmanim__nav {
  display: flex; align-items: center; justify-content: center;
  gap: 12px;
  margin-bottom: clamp(18px, 3vw, 28px);
}
.t26-zmanim__nav-btn {
  width: 38px; height: 38px;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--primary);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; line-height: 1;
  flex-shrink: 0;
  transition: background .15s ease, border-color .15s ease;
}
.t26-zmanim__nav-btn:hover {
  background: color-mix(in oklab, var(--primary) 8%, var(--surface));
  border-color: color-mix(in oklab, var(--primary) 30%, var(--line));
}
.t26-zmanim__nav-label {
  font-size: 15px; font-weight: 600;
  color: var(--ink);
  min-width: 220px; text-align: center; line-height: 1.3;
  text-transform: capitalize;
}
.t26-zmanim__nav-label span {
  display: block;
  font-size: 12px; font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  text-transform: none;
}
.t26-zmanim__today {
  display: block;
  margin: 0 auto clamp(18px, 3vw, 28px);
  background: none; border: 0;
  font-size: 13px; font-weight: 600;
  color: var(--primary);
  cursor: pointer;
  text-decoration: underline;
}

/* ── Carte des zmanim du jour ── */
.t26-zmanim__card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  margin-bottom: clamp(20px, 3vw, 32px);
}
.t26-zmanim__card-head {
  padding: 16px 20px;
  background:
    radial-gradient(80% 100% at 100% 0%, color-mix(in oklab, var(--secondary) 14%, transparent) 0%, transparent 70%),
    color-mix(in oklab, var(--primary) 8%, var(--surface));
  border-bottom: 1px solid var(--line);
  text-align: center;
}
.t26-zmanim__card-head-day {
  font-family: var(--font-display);
  font-size: 17px; font-weight: 700;
  color: var(--ink);
  text-transform: capitalize;
  line-height: 1.2;
}
.t26-zmanim__card-head-hdate {
  font-size: 13px; color: var(--ink-3);
  margin-top: 3px;
}
.t26-zmanim__list { list-style: none; padding: 0; margin: 0; }
.t26-zmanim__item {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 13px 20px;
  border-bottom: 1px solid var(--line);
}
.t26-zmanim__item:last-child { border-bottom: 0; }
.t26-zmanim__item--next {
  background: color-mix(in oklab, var(--primary) 7%, var(--surface));
}
.t26-zmanim__item-name {
  font-size: 14px; color: var(--ink-2, var(--ink));
  flex: 1; min-width: 0;
}
.t26-zmanim__item--next .t26-zmanim__item-name {
  color: var(--primary); font-weight: 600;
}
.t26-zmanim__badge {
  font-size: 11px; font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--primary);
  color: var(--primary-contrast);
  white-space: nowrap;
}
.t26-zmanim__item-time {
  font-family: var(--font-display);
  font-size: 16px; font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.t26-zmanim__item--next .t26-zmanim__item-time { color: var(--primary); }

/* ── Carte du prochain Chabbat ── */
.t26-zmanim__shabbat {
  background: var(--surface);
  border: 1px solid color-mix(in oklab, var(--primary) 24%, var(--line));
  border-radius: var(--r-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}
.t26-zmanim__shabbat-head {
  background: var(--primary);
  color: var(--primary-contrast);
  padding: 14px 20px;
  text-align: center;
  font-family: var(--font-display);
  font-size: 16px; font-weight: 700;
}
.t26-zmanim__shabbat-head span {
  display: block;
  font-family: var(--font-body);
  font-size: 12px; font-weight: 400;
  opacity: .85;
  margin-top: 3px;
}
.t26-zmanim__shabbat-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 15px 20px;
  border-bottom: 1px solid var(--line);
}
.t26-zmanim__shabbat-row:last-child { border-bottom: 0; }
.t26-zmanim__shabbat-label { font-size: 14px; color: var(--ink-2, var(--ink)); }
.t26-zmanim__shabbat-label small { display: block; font-size: 11px; color: var(--ink-3); margin-top: 2px; }
.t26-zmanim__shabbat-value {
  font-family: var(--font-display);
  font-size: 19px; font-weight: 700;
  color: var(--primary);
  font-variant-numeric: tabular-nums;
}

@media (max-width: 560px) {
  .t26-zmanim__nav-label { min-width: 0; flex: 1; font-size: 14px; }
  .t26-zmanim__item { padding: 12px 15px; }
  .t26-zmanim__item-name { font-size: 13px; }
  .t26-zmanim__shabbat-row { padding: 13px 15px; }
}
`;

export default function JewishZmanim({ context, loading }) {
    const { org, config, langValue } = context;
    const locale = langValue || "fr";
    const labels = getZmanimLabels();

    const [date, setDate] = React.useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });

    const info = useZmanim(org, date);
    const tz = info?.tz;

    const goDay = (delta) => {
        setDate((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() + delta);
            return d;
        });
    };

    const goToday = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        setDate(d);
    };

    const isToday = React.useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d.getTime() === date.getTime();
    }, [date]);

    const city = org.ville || org.name;
    const hdateLabel = info?.hdate
        ? `${info.hdate.getDate()} ${info.hdate.getMonthName()} ${info.hdate.getFullYear()}`
        : null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <Meta
                context={context}
                title={t`Horaires halakhiques — ${city}`}
                description={config["meta.description"] || t`Zmanim et horaires halakhiques quotidiens à ${city}`}
            />
            {loading && <Loader />}

            <PageHeader
                title={t`Horaires halakhiques`}
                subtitle={t`Les horaires du jour calculés pour ${city}`}
            />

            <div className="t26-zmanim">
                {/* ── Sélecteur de date ── */}
                <div className="t26-zmanim__nav">
                    <button
                        type="button"
                        className="t26-zmanim__nav-btn t26-flip-rtl"
                        onClick={() => goDay(-1)}
                        aria-label={t`Jour précédent`}
                    >‹</button>
                    <div className="t26-zmanim__nav-label">
                        {fmtGreg(date, locale, tz)}
                        {hdateLabel && <span>{hdateLabel}</span>}
                    </div>
                    <button
                        type="button"
                        className="t26-zmanim__nav-btn t26-flip-rtl"
                        onClick={() => goDay(1)}
                        aria-label={t`Jour suivant`}
                    >›</button>
                </div>

                {!isToday && (
                    <button type="button" className="t26-zmanim__today" onClick={goToday}>
                        <Trans>Revenir à aujourd'hui</Trans>
                    </button>
                )}

                {/* Position de l'org inconnue → aucun calcul possible. */}
                {info === null && (
                    <EmptyState
                        icon="IconClock"
                        title={t`Horaires indisponibles`}
                        subtitle={t`La position de l'organisation n'est pas renseignée.`}
                    />
                )}

                {info && info.zmanim.length > 0 && (
                    <div className="t26-zmanim__card">
                        <div className="t26-zmanim__card-head">
                            <div className="t26-zmanim__card-head-day">{fmtGreg(date, locale, tz)}</div>
                            {hdateLabel && <div className="t26-zmanim__card-head-hdate">{hdateLabel}</div>}
                        </div>
                        <ul className="t26-zmanim__list">
                            {info.zmanim.map((z, i) => (
                                <li key={i} className={`t26-zmanim__item${z.next ? " t26-zmanim__item--next" : ""}`}>
                                    <span className="t26-zmanim__item-name">{labels[z.nom] || z.nom}</span>
                                    {z.next && (
                                        <span className="t26-zmanim__badge"><Trans>Prochain</Trans></span>
                                    )}
                                    <span className="t26-zmanim__item-time">{fmtTime(z.time, locale, tz)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* ── Prochain Chabbat ── */}
                {info?.period && (
                    <div className="t26-zmanim__shabbat">
                        <div className="t26-zmanim__shabbat-head">
                            {restPeriodTitle(info.period, locale)}
                            <span>
                                {fmtShortDate(info.period.start, locale, tz)} — {fmtShortDate(info.period.end, locale, tz)}
                            </span>
                        </div>
                        {info.period.schedule.map((item, index) => (
                            <div className="t26-zmanim__shabbat-row" key={index}>
                                <span className="t26-zmanim__shabbat-label">
                                    {restPeriodRowLabel(item, info.period)}
                                    <small>{fmtShortDate(item.at, locale, tz)}</small>
                                </span>
                                <span className="t26-zmanim__shabbat-value">{fmtTime(item.at, locale, tz)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
